import {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Page, route, useSearch} from 'b302-frontend-library';

import {FilterParams} from '../../../networking/repos/UniversityRepository';
import UniversityFilter, {FilterChangeEvent} from '../partials/university-filter/UniversityFilter';
import University from '../../../networking/models/University';
import {error} from '../../../utils/Toast';
import HanButton from '../../utils/HanButton';
import UniversityList from '../partials/university/UniversityList';
import FilterPageLeft from '../partials/filter/FilterPageLeft';
import FilterPageFilter from '../partials/filter/FilterPageFilter';
import FilterPageRight from '../partials/filter/FilterPageRight';
import FilterPageHeader from '../partials/filter/FilterPageHeader';
import FilterPageContent from '../partials/filter/FilterPageContent';
import FilterPageFooter from '../partials/filter/FilterPageFooter';
import {useFilterMenu} from '../../../utils/Filter';
import {useCountries} from '../../../contexts/CountryContext';
import Breadcrumbs from '../../utils/Breadcrumbs';
import {useUniversityIndexQueryConfig} from '../../query/University/Query';
import {useQuery} from '@tanstack/react-query';
import {useFavorites} from "../../../contexts/FavoritesContext";
import FavoriteButton from "../partials/university/FavoriteButton";

export interface UniversityFilterParams extends FilterParams {
    activeFilters?: number;
}

interface Props {
    title: string;
    favorites?: boolean;
}

const UniversitiesPage: FunctionComponent<Props> = ({title, favorites}) => {
    const navigate = useNavigate();

    const {countries} = useCountries();

    const params = useMemo(() => {
        return new URLSearchParams(window.location.search);
    }, []);

    const initialSelectedCountries = useMemo(() => {
        const continentIds = params.get('continents')?.split('').filter(id => !!id).map(id => parseInt(id)) ?? [];
        const continentCountryIds = countries.filter(country => continentIds?.includes(country.continent!.id)).map(country => country.id!);

        const countryIds = params.get('countries')?.split('').filter(id => !!id).map(id => parseInt(id)) ?? [];
        return [...new Set([...continentCountryIds, ...countryIds])]
    }, [countries, params]);

    const [searchQuery, search] = useSearch(params.get('searchQuery') ?? '');

    const favoriteUniversities = useFavorites().favoriteUniversities;

    const [filters, setFilters] = useState<UniversityFilterParams>({
        favorites,
        countries:
        initialSelectedCountries,
        searchQuery: searchQuery,
        min_cost: 0,
        max_cost: 3000
    });
    const [filterMenuOpen, toggleFilterMenu] = useFilterMenu();

    const [comparing, setComparing] = useState<boolean>(false);
    const [selectedUniversities, setSelectedUniversities] = useState<number[]>([]);
    const [refreshKey, setRefreshKey] = useState<string>('');

    const minCompareCount = 2;
    const universityCompareCount = 3;

    const [universityConfig, setPage] = useUniversityIndexQueryConfig(filters);
    const universityQuery = useQuery(universityConfig);

    useEffect(() => {
        setRefreshKey(Object.values(filters).join('-'));

        for (const filter of Object.keys(filters)) {
            if (filter === 'active_filters') continue;

            params.set(filter, filters[filter as keyof UniversityFilterParams]?.toString() ?? '');
        }

        window.history.replaceState(null, '', `?${params.toString()}`);
    }, [filters]);

    const stopComparing = useCallback(() => {
        setSelectedUniversities([]);
        setComparing(false);
    }, []);

    const onFilter = ({countries, minCosts, maxCosts, reviewScore, activeFilters}: FilterChangeEvent) => {
        stopComparing();

        setFilters(f => ({
            ...f,
            activeFilters,
            countries,
            min_cost: minCosts,
            max_cost: maxCosts,
            rating: reviewScore
        }));
    }

    const onSearch = (value: any) => {
        search(value, () => setFilters(filters => ({...filters, searchQuery: value})));
    };

    const toggleComparing = () => {
        if (comparing) {
            stopComparing();
        } else {
            setComparing(true);
        }
    };

    const onUniversitySelect = (university: University) => {
        const newSelectedUniversities = [...selectedUniversities];
        const index = newSelectedUniversities.findIndex(id => id === university.id!);

        if (index === -1 && newSelectedUniversities.length === universityCompareCount) {
            error('You can select 3 universities max.');
            return;
        } else if (index === -1) {
            newSelectedUniversities.push(university.id!);
        } else {
            newSelectedUniversities.splice(index, 1);
        }

        setSelectedUniversities(newSelectedUniversities);
    };

    const renderCompareButton = () => {
        return (
            <HanButton onClick={compareUniversities}
                       indicatorText={`${selectedUniversities.length}/${universityCompareCount}`}>
                Compare
            </HanButton>
        );
    };

    const compareUniversities = () => {
        if (selectedUniversities.length < minCompareCount) return;

        navigate(route('compare-universities', {ids: selectedUniversities.join(',')}));
    };

    const getFilterButtonText = () => {
        if (filters.activeFilters && filters.activeFilters > 0) {
            return filters?.activeFilters?.toString();
        }

        return '+';
    };

    if (params.has('continents') && !initialSelectedCountries.length) {
        return null;
    }

    if (!universityQuery.data) {
        return null;
    }

    return (
        <Page className={'filter-page'}>
            <FilterPageLeft menuOpen={filterMenuOpen}>
                <FilterPageFilter toggleMenu={toggleFilterMenu} activeFilters={filters.activeFilters}>
                    <UniversityFilter
                        countries={universityQuery.data.data.countries}
                        onFilterChange={onFilter}
                        prefilledFilterValues={{
                            minCosts: params.get('max_cost') ? parseInt(params.get('min_cost')!) : undefined,
                            maxCosts: params.get('min_cost') ? parseInt(params.get('max_cost')!) : undefined,
                            reviewScore: params.get('rating') ? parseInt(params.get('rating')!) : undefined,
                            selectedCountries: initialSelectedCountries,
                        }}/>

                    <div className={'filter-page__filter-button'}>
                        <HanButton onClick={toggleFilterMenu}>Show {universityQuery.data.total} results</HanButton>
                    </div>
                </FilterPageFilter>

                {comparing &&
                    <div className={'universities-page__compare-button'}>
                        {renderCompareButton()}
                    </div>
                }
            </FilterPageLeft>

            <FilterPageRight>
                <FilterPageHeader title={`${title} (${universityQuery.data.total})`} searchQuery={searchQuery}
                                  onSearch={onSearch} searchPlaceholder={'Find a university'}>
                    <div
                        className={favorites ? 'universities-page__compare universities-page__compare--reverse' : 'universities-page__compare'}>
                        <HanButton onClick={toggleComparing} close={comparing}>
                            {comparing
                                ? 'Stop comparing'
                                : 'Compare universities'
                            }
                        </HanButton>

                        {!favorites ?
                            <FavoriteButton isGray={favoriteUniversities.length < 1}></FavoriteButton> :
                            <HanButton onClick={() => navigate(-1)} backArrow={true}>Back</HanButton>}
                    </div>
                </FilterPageHeader>

                {/* br-tag is used because FilterPageRight is used on multiple pages with different styling.
                        Applying style would change it everywhere. This br-tag does not*/}
                <br></br>

                <FilterPageContent>
                    <Breadcrumbs
                        breadcrumbs={
                            [
                                {name: 'Home', url: '/'},
                                {name: 'Universities', url: '/universities'}
                            ]
                        }
                    />

                    <UniversityList
                        refreshKey={refreshKey}
                        universities={universityQuery.data.data.universities}
                        selectedUniversities={selectedUniversities}
                        onCompareClick={comparing ? onUniversitySelect : undefined}/>
                </FilterPageContent>

                <FilterPageFooter
                    total={universityQuery.data.total}
                    pageCount={universityQuery.data.lastPage}
                    currentPage={universityQuery.data.currentPage}
                    onPageChange={setPage}>

                    {comparing
                        ? renderCompareButton()
                        : <HanButton onClick={toggleFilterMenu} modifiers={['universities-page__button--big']}
                                     indicatorText={getFilterButtonText()}>Filter programs</HanButton>
                    }
                </FilterPageFooter>
            </FilterPageRight>
        </Page>
    );
};

export default UniversitiesPage;
